import { Col } from 'react-bootstrap';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button from 'components/button/Button';
import { VrboCancellationPolicy } from 'models/Channels';
import { PropertySettingsMainTabFormValues } from '../../PropertySettingsMainTab.schema';
import PropertyVrboSettingsPolicyPeriodField from './PropertyVrboSettingsPolicyPeriodField';

const PropertyVrboSettingsPolicyPeriodsField = () => {
  const { t } = useTranslation();
  // Using `setValue` instead of `useFieldArray` to avoid marking all fields as dirty in
  // `usePropertyMainSettingsUpdatePayload`. See related issues:
  // https://github.com/react-hook-form/react-hook-form/issues/12784s
  // https://github.com/react-hook-form/react-hook-form/issues/11929
  const { control, setValue } =
    useFormContext<PropertySettingsMainTabFormValues>();

  const cancellationPolicy = useWatch({
    control,
    name: 'vrboSettings.cancellationPolicy',
  });

  const customCancellationPolicies =
    useWatch({ control, name: 'vrboSettings.customCancellationPolicies' }) ??
    [];

  const onAddPolicyPeriod = () => {
    setValue(
      'vrboSettings.customCancellationPolicies',
      [
        ...customCancellationPolicies,
        { id: crypto.randomUUID(), refundPercentage: 0, refundThreshold: 0 },
      ],
      { shouldDirty: true },
    );
  };

  const onDeletePolicyPeriod = (id: string) => {
    setValue(
      'vrboSettings.customCancellationPolicies',
      customCancellationPolicies.filter((current) => current?.id !== id),
      { shouldDirty: true },
    );
  };

  if (cancellationPolicy !== VrboCancellationPolicy.CUSTOM) {
    return null;
  }

  return (
    <>
      {customCancellationPolicies.map(
        (field, index) =>
          field && (
            <PropertyVrboSettingsPolicyPeriodField
              index={index}
              key={field.id}
              onDelete={() => onDeletePolicyPeriod(field.id!)}
            />
          ),
      )}

      {customCancellationPolicies.length < 3 && (
        <Col className="add-policy-period-column" xsOffset={4} smOffset={5}>
          <Button
            bsStyle="link"
            data-testid="vrbo-add-policy-period-button"
            onClick={onAddPolicyPeriod}
          >
            {t(
              'pageProperty.mainSettings.vrboSettings.policyPeriods.addPolicyPeriod',
            )}
          </Button>
        </Col>
      )}
    </>
  );
};

export default PropertyVrboSettingsPolicyPeriodsField;
